/**
 * Datasets the chart builder can connect to, their fields, and the binding → query contract.
 * Pure and DOM-free: the Worker imports this file to validate and run the same queries (worker/src/dashboard-overview-query.js).
 */
export type FieldType = "text" | "number" | "currency" | "date" | "category" | "ward" | "person" | "org";
export interface Field { id: string; label: string; type: FieldType }
export type DatasetId = "contributions" | "rollCalls" | "lobbyingFilings" | "forecasts" | "meetings" | "legislation" | "sponsorships" | "people" | "organizations" | "wardResponses" | "testimony" | "wards" | "actions";
export type Group = "resident" | "office" | "advocacy";

export interface DatasetDef {
  id: DatasetId; label: string; icon: string; source: string;
  live: boolean;                 // updates over the org channel
  sample?: boolean;              // shows "Sample data" until the backend ships it
  groups?: Group[];              // who can use it; undefined = everyone
  fields: Field[];
}

const f = (id: string, type: FieldType, label = id) => ({ id, label, type });

/** Every dataset the app can chart. Keep in sync with the Worker's `/api/datasets/:id` handlers. */
export const DATASETS: Record<DatasetId, DatasetDef> = {
  contributions: { id: "contributions", label: "Contributions", icon: "banknote", source: "ISBE + Board of Ethics", live: true,
    fields: [f("recipient", "person"), f("amount", "currency"), f("date", "date"), f("contributorType", "category", "contributor type"),
      f("client", "org"), f("ward", "ward"), f("lobbyist", "person"), f("committee", "category")] },
  rollCalls: { id: "rollCalls", label: "Roll calls", icon: "gavel", source: "City Clerk", live: true,
    fields: [f("matter", "text"), f("member", "person"), f("vote", "category"), f("date", "date"), f("ward", "ward"), f("committee", "category"), f("margin", "number")] },
  lobbyingFilings: { id: "lobbyingFilings", label: "Lobbying filings", icon: "file-text", source: "Board of Ethics", live: false,
    fields: [f("client", "org"), f("lobbyist", "person"), f("topic", "category"), f("quarter", "date"), f("filings", "number"), f("position", "category")] },
  forecasts: { id: "forecasts", label: "Forecasts", icon: "sparkles", source: "Our model", live: true, sample: true,
    fields: [f("member", "person"), f("item", "text"), f("status", "category"), f("probability", "number"), f("ward", "ward")] },
  meetings: { id: "meetings", label: "Meetings", icon: "calendar", source: "City Clerk", live: true,
    fields: [f("body", "category"), f("date", "date"), f("type", "category"), f("items", "number")] },
  legislation: { id: "legislation", label: "Legislation", icon: "scroll-text", source: "City Clerk", live: true,
    fields: [f("record", "text"), f("category", "category"), f("stage", "category"), f("sponsor", "person"), f("introduced", "date"), f("committee", "category"), f("type", "category")] },
  // Who files what: every non-routine matter this term by its filing sponsor (City Clerk).
  sponsorships: { id: "sponsorships", label: "Sponsorships", icon: "pen-line", source: "City Clerk", live: true,
    fields: [f("sponsor", "person"), f("ward", "ward"), f("category", "category"), f("type", "category"), f("committee", "category"), f("year", "date")] },
  people: { id: "people", label: "People", icon: "users", source: "Our records", live: false,
    fields: [f("name", "person"), f("ward", "ward"), f("leaning", "number"), f("attendance", "number")] },
  organizations: { id: "organizations", label: "Organizations", icon: "building-2", source: "Board of Ethics + ISBE", live: false,
    fields: [f("name", "org"), f("kind", "category"), f("filings", "number"), f("toAlderpersons", "currency", "to alderpersons"),
      f("items", "number"), f("topic", "category")] },
  wardResponses: { id: "wardResponses", label: "Ward responses", icon: "message-square", source: "How would you vote?", live: true, sample: true, groups: ["office"],
    fields: [f("ward", "ward"), f("item", "text"), f("stance", "category"), f("support", "number"), f("oppose", "number"), f("date", "date")] },
  // Overview (screenshots/70 "Constituent testimony by topic"): public comments from the meeting analyses (appearances).
  testimony: { id: "testimony", label: "Public testimony", icon: "mic", source: "Meetings · transcripts", live: false,
    fields: [f("topic", "category"), f("stance", "category"), f("ward", "ward"), f("body", "category"), f("date", "date")] },
  // Census by ward (site/data/public/ward_acs.json): one row per ward; the measure's field is the number shown (sum of one
  // row is the row), so `groupBy=ward&measure=sum:renterShare` is renters by ward, drawn best as a map.
  wards: { id: "wards", label: "Wards (census)", icon: "map", source: "U.S. Census ACS", live: false,
    fields: [f("ward", "ward"), f("population", "number"), f("medianIncome", "currency", "median income"), f("medianAge", "number", "median age"),
      f("renterShare", "number", "renters"), f("bachelorsShare", "number", "bachelor's degrees"), f("povertyRate", "number", "poverty rate")] },
  // One matter's path through the Council (City Clerk matter_actions, with its roll calls): `filter=matter:is:<record>` is
  // required; `groupBy=date` is the timeline (one row per action, oldest first), `action` or `by` counts them.
  actions: { id: "actions", label: "A matter's actions", icon: "waypoints", source: "City Clerk", live: true,
    fields: [f("matter", "text"), f("date", "date"), f("action", "category"), f("by", "category", "taken by")] },
};

export const datasetsFor = (group: Group) => Object.values(DATASETS).map((d) => ({ ...d, locked: !!d.groups && !d.groups.includes(group) }));

export type Slot = "category" | "value" | "split" | "tooltip";
export type Agg = "sum" | "count" | "avg" | "max" | "min";
const NUMERIC: FieldType[] = ["number", "currency"];

/** Whether a field can go in a slot; the reason is shown on dimmed slots while dragging. */
export function acceptsField(slot: Slot, field: Field): { ok: true } | { ok: false; reason: string } {
  switch (slot) {
    case "value": return NUMERIC.includes(field.type) ? { ok: true } : { ok: false, reason: "Needs a number (or use Count)" };
    case "category": return NUMERIC.includes(field.type) ? { ok: false, reason: "Numbers go in Value" } : { ok: true };
    case "split": return ["category", "person", "org", "ward"].includes(field.type) ? { ok: true } : { ok: false, reason: "Needs a few groups, like a type or status" };
    case "tooltip": return { ok: true };
  }
}

export interface Filter { field: string; op: "is" | "isNot" | "in" | "gte" | "lte"; value: string | number | (string | number)[] }

export interface DataBinding {
  datasetId: DatasetId;
  mapping: { category?: string; value: { field?: string; agg: Agg }; split?: string; tooltip: string[] };
  filters: Filter[];
  sort: "valueDesc" | "valueAsc" | "az" | "none";
  topN?: number;
  groupRest: boolean;           // fold the rest into "Others"
  refresh: "live" | "hourly" | "open";
  followViewer?: "ward";        // adds ward = {viewer.ward}
}

/** Problems that block "Add to overview" (shown at the top of the Data tab). */
export function bindingProblems(b: DataBinding): string[] {
  const ds = DATASETS[b.datasetId];
  const field = (id?: string) => (id ? ds.fields.find((x) => x.id === id) : undefined);
  const out: string[] = [];
  if (b.mapping.value.agg !== "count") {
    const v = field(b.mapping.value.field);
    if (!v) out.push("Pick a value, or use Count.");
    else if (!NUMERIC.includes(v.type)) out.push(`${v.label} isn’t a number. Use Count, or pick a number field.`);
  }
  for (const [slot, id] of [["category", b.mapping.category], ["split", b.mapping.split]] as const) {
    if (!id) continue;
    const x = field(id);
    if (!x) out.push(`${id} isn’t in ${ds.label}.`);
    else { const r = acceptsField(slot, x); if (!r.ok) out.push(`${x.label}: ${r.reason}`); }
  }
  for (const flt of b.filters) if (!field(flt.field)) out.push(`Filter on ${flt.field} doesn’t match ${ds.label}.`);
  if (b.topN !== undefined && (b.topN < 1 || b.topN > 50)) out.push("Show between 1 and 50.");
  return out;
}

/** The dataset request (`/api/datasets/:id?…`). Viewer tokens are filled in on the server, never trusted from the client. */
export function bindingToQuery(b: DataBinding): string {
  const q = new URLSearchParams();
  if (b.mapping.category) q.set("groupBy", b.mapping.category);
  q.set("measure", b.mapping.value.agg === "count" ? "count" : `${b.mapping.value.agg}:${b.mapping.value.field}`);
  if (b.mapping.split) q.set("split", b.mapping.split);
  if (b.mapping.tooltip.length) q.set("extra", b.mapping.tooltip.join(","));
  for (const x of b.filters) q.append("filter", `${x.field}:${x.op}:${Array.isArray(x.value) ? x.value.join("|") : x.value}`);
  if (b.followViewer === "ward") q.append("filter", "ward:is:{viewer.ward}");
  if (b.sort !== "none") q.set("sort", { valueDesc: "-value", valueAsc: "value", az: "label" }[b.sort]);
  if (b.topN) q.set("top", String(b.topN));
  if (b.groupRest) q.set("rest", "others");
  return `/api/datasets/${b.datasetId}?${q.toString().replace(/%7B/g, "{").replace(/%7D/g, "}").replace(/%3A/g, ":").replace(/%2C/g, ",").replace(/%7C/g, "|")}`;
}
